/**
 * Cores das Novidades. A cor da marca vem do tenant (primaryColor(): roxo na Almode, verde na
 * Vestcasa) e o resto é derivado dela, nos temas claro e escuro. As variáveis vão no elemento raiz da
 * tela por :style, porque o customProperties do Vuetify está desligado.
 *
 * Todo texto sai com contraste de pelo menos 4,5:1 sobre o fundo em que aparece: a cor da marca é
 * escurecida (claro) ou clareada (escuro) até chegar lá. O verde da Vestcasa, puro, não chega.
 */

export interface AreaDeNovidade {
	/** Texto no tema claro. */
	cor: string
	/** Fundo no tema claro. */
	fundo: string
	/** Texto e contorno no tema escuro. */
	corNoEscuro: string
	icone: string
}

/** Paleta categórica das áreas (etiquetas da publicação). A ordem é a dos filtros. */
export const AREAS: Record<string, AreaDeNovidade> = {
	'Caixa': { cor: '#2B5FA3', fundo: '#EAF1FB', corNoEscuro: '#8DB4EC', icone: 'mdi-point-of-sale' },
	'Fiscal': { cor: '#8F521A', fundo: '#FCEFE3', corNoEscuro: '#E9B27C', icone: 'mdi-file-document-outline' },
	'Cashback': { cor: '#37701A', fundo: '#EAF7E1', corNoEscuro: '#9FD37E', icone: 'mdi-cash-refund' },
	'Títulos/Crediário': { cor: '#6A2FA0', fundo: '#F1E9FA', corNoEscuro: '#C7A3EE', icone: 'mdi-credit-card-clock-outline' },
	'Integrações': { cor: '#146B66', fundo: '#E4F5F4', corNoEscuro: '#7FD3CC', icone: 'mdi-connection' },
	'Demonstração': { cor: '#585858', fundo: '#EDEDED', corNoEscuro: '#BDBDBD', icone: 'mdi-hanger' },
	'Sistema': { cor: '#455A64', fundo: '#ECEFF1', corNoEscuro: '#B0BEC5', icone: 'mdi-monitor-dashboard' },
}

const AREA_SEM_COR: AreaDeNovidade = { cor: '#585858', fundo: '#EDEDED', corNoEscuro: '#BDBDBD', icone: 'mdi-tag-outline' }

export const area = (nome: string): AreaDeNovidade => AREAS[nome] || AREA_SEM_COR

/** Áreas que aparecem na lista, na ordem da paleta; as desconhecidas vão no fim, em ordem alfabética. */
export function areasPresentes(listas: string[][]): string[] {
	const todas = new Set(listas.flat())
	const conhecidas = Object.keys(AREAS).filter(a => todas.has(a))
	const outras = [...todas].filter(a => !AREAS[a]).sort((a, b) => a.localeCompare(b, 'pt-BR'))
	return [...conhecidas, ...outras]
}

type Rgb = number[]

function paraRgb(hex: string): Rgb {
	let h = hex.replace('#', '').trim()
	if (h.length === 3) h = h.split('').map(c => c + c).join('')
	const n = parseInt(h.slice(0, 6), 16)
	return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function paraHex([r, g, b]: Rgb): string {
	return '#' + [r, g, b].map(c => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('').toUpperCase()
}

/** Mistura `peso` da cor `a` com o resto da cor `b`. */
export function misturar(a: string, b: string, peso: number): string {
	const x = paraRgb(a)
	const y = paraRgb(b)
	return paraHex([0, 1, 2].map(i => x[i] * peso + y[i] * (1 - peso)))
}

function luminancia(hex: string) {
	const [r, g, b] = paraRgb(hex).map(c => {
		const s = c / 255
		return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
	})
	return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contraste(a: string, b: string): number {
	const [claro, escuro] = [luminancia(a), luminancia(b)].sort((x, y) => y - x)
	return (claro + 0.05) / (escuro + 0.05)
}

/** Aproxima a cor do preto (fundo claro) ou do branco (fundo escuro) até o contraste mínimo. */
export function legivel(cor: string, fundo: string, minimo = 4.5): string {
	const alvo = luminancia(fundo) > 0.4 ? '#000000' : '#FFFFFF'
	let resultado = cor
	for (let passo = 1; passo <= 20 && contraste(resultado, fundo) < minimo; passo++) {
		resultado = misturar(alvo, cor, passo * 0.05)
	}
	return resultado
}

/** Variáveis CSS da tela de Novidades, para o tema e a marca do tenant. */
export function tokensDeTema(marca: string, escuro: boolean): Record<string, string> {
	if (!escuro) {
		const fundo = '#FFFFFF'
		const fundo2 = misturar(marca, '#FFFFFF', 0.035)
		const suave = misturar(marca, '#FFFFFF', 0.09)
		return {
			'--nov-forte': misturar(marca, '#151418', 0.05),
			'--nov-texto': misturar(marca, '#2A2830', 0.06),
			'--nov-texto-2': legivel(misturar(marca, '#5E5A66', 0.06), suave),
			'--nov-texto-3': legivel(misturar(marca, '#75707D', 0.06), suave),
			'--nov-linha': misturar(marca, '#E3E2E8', 0.08),
			'--nov-fundo': fundo,
			'--nov-fundo-2': fundo2,
			'--nov-fundo-3': misturar(marca, '#FFFFFF', 0.07),
			'--nov-primaria': legivel(marca, suave),
			'--nov-primaria-suave': suave,
			'--nov-primaria-ponto': marca,
			'--nov-sombra': '0 1px 2px rgba(23, 20, 29, .06), 0 2px 8px rgba(23, 20, 29, .05)',
			'--nov-sombra-forte': '0 2px 4px rgba(23, 20, 29, .08), 0 8px 24px rgba(23, 20, 29, .08)',
			'--nov-marcador': '#C5221F',
			'--nov-marcador-texto': '#FFFFFF',
			'--nov-aviso-fundo': '#FFF8E6',
			'--nov-aviso-borda': '#E0A100',
			'--nov-aviso-texto': '#6E4B00',
		}
	}
	const fundo = '#1E1E1E'
	const suave = misturar(marca, '#1E1E1E', 0.22)
	return {
		'--nov-forte': '#F4F2F7',
		'--nov-texto': '#DAD6E0',
		'--nov-texto-2': '#ABA6B3',
		'--nov-texto-3': legivel('#8F8A99', suave),
		'--nov-linha': misturar(marca, '#343434', 0.1),
		'--nov-fundo': fundo,
		'--nov-fundo-2': '#121212',
		'--nov-fundo-3': misturar(marca, '#262626', 0.14),
		'--nov-primaria': legivel(misturar(marca, '#FFFFFF', 0.55), suave),
		'--nov-primaria-suave': suave,
		'--nov-primaria-ponto': legivel(misturar(marca, '#FFFFFF', 0.55), fundo, 3),
		'--nov-sombra': '0 1px 2px rgba(0, 0, 0, .4)',
		'--nov-sombra-forte': '0 2px 6px rgba(0, 0, 0, .5), 0 8px 24px rgba(0, 0, 0, .35)',
		'--nov-marcador': '#F28B82',
		'--nov-marcador-texto': '#2B0B08',
		'--nov-aviso-fundo': '#2B2414',
		'--nov-aviso-borda': '#E0A100',
		'--nov-aviso-texto': '#F2C14E',
	}
}
